import { useState } from 'react';
import { KeyRound, X } from 'lucide-react';
import { api } from '../api/client';
import { useT } from '../i18n';
import { describeError } from '../utils/format';
import { Modal } from './Modal';

interface ChangePasswordDialogProps {
  onClose: () => void;
}

/** E14-US4 — vlastní změna hesla; existující sessions na jiných kartách zůstávají platné. */
export function ChangePasswordDialog({ onClose }: ChangePasswordDialogProps) {
  const t = useT();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.changePassword(currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
    } catch (caught) {
      setError(describeError(caught, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t('password.change.title')}
      onClose={onClose}
      actions={
        <>
          <button type="button" onClick={onClose}>
            <X size={16} />
            {t('common.close')}
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy || !currentPassword || !newPassword}
            onClick={() => void submit()}
          >
            <KeyRound size={16} />
            {t('password.change.submit')}
          </button>
        </>
      }
    >
      <div className="stack">
        {success && <div className="notice notice--success">{t('password.change.success')}</div>}
        {error && <div className="notice notice--error">{error}</div>}
        <div className="field">
          <label htmlFor="change-password-current">{t('password.change.currentLabel')}</label>
          <input
            id="change-password-current"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="change-password-new">{t('password.change.newLabel')}</label>
          <input
            id="change-password-new"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
